import {
  Archive,
  AlertCircle,
  BarChart3,
  Copy,
  DollarSign,
  Download,
  Eye,
  Loader2,
  Package,
  Star,
  Undo2,
  UploadCloud,
} from "lucide-react";
import { useState } from "react";
import { Link } from "react-router";
import {
  updateCreatorPackageLifecycle,
  useCreatorDashboard,
  type CreatorDashboardData,
  type CreatorPackage,
} from "@/hooks/use-marketplace";
import { EmptyState } from "@/components/ui/empty-state";
import { formatCurrency, formatDate, formatNumber } from "@/lib/i18n";

function CreatorSkeleton() {
  return (
    <div className="space-y-6">
      <div className="h-8 w-64 animate-pulse rounded bg-muted" />
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
        {Array.from({ length: 4 }, (_, index) => (
          <div key={index} className="h-24 animate-pulse rounded-lg bg-muted" />
        ))}
      </div>
      <div className="h-64 animate-pulse rounded-lg bg-muted" />
    </div>
  );
}

const STATUS_LABELS: Record<CreatorPackage["status"], { label: string; tone: string }> = {
  draft: { label: "Draft", tone: "bg-muted text-muted-foreground" },
  review: { label: "In review", tone: "bg-[var(--color-warning)]/10 text-[var(--color-warning)]" },
  published: { label: "Published", tone: "bg-[var(--color-success)]/10 text-[var(--color-success)]" },
  rejected: { label: "Needs attention", tone: "bg-[var(--color-destructive)]/10 text-[var(--color-destructive)]" },
  archived: { label: "Archived", tone: "bg-muted text-muted-foreground" },
  suspended: { label: "Suspended", tone: "bg-[var(--color-destructive)]/10 text-[var(--color-destructive)]" },
};

function StatusBadge({ status }: { status: CreatorPackage["status"] }) {
  const config = STATUS_LABELS[status];
  return (
    <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${config.tone}`}>
      {config.label}
    </span>
  );
}

function getVersionOfferHint(pkg: CreatorPackage) {
  switch (pkg.status) {
    case "published":
      return `Buyers installing now receive v${pkg.version}. Existing installs stay unchanged until they explicitly reinstall a newer marketplace version.`;
    case "review":
      return `The review queue is evaluating v${pkg.version}. Approval publishes that snapshot as the next buyer-facing offer.`;
    case "rejected":
      return `Revise v${pkg.version}, then resubmit it as the next marketplace offer. Existing buyer installs remain unchanged.`;
    case "archived":
      return `This version is off the live marketplace right now. Resubmit it when you want to offer v${pkg.version} again.`;
    case "suspended":
      return `This version is off the live marketplace because platform review suspended the listing. Buyer installs remain unchanged.`;
    case "draft":
    default:
      return `Prepare v${pkg.version} privately, then submit it when you are ready to make that version available as the next marketplace offer.`;
  }
}

function getLifecycleNote(pkg: CreatorPackage) {
  if ((pkg.status === "rejected" || pkg.status === "archived" || pkg.status === "suspended") && pkg.moderationReason) {
    return pkg.moderationReason;
  }
  return pkg.lifecycle.description;
}

function getLifecycleTimestamp(pkg: CreatorPackage) {
  if (pkg.status === "review" && pkg.reviewedAt) {
    return `Submitted ${formatDate(pkg.reviewedAt)}`;
  }

  if (pkg.status === "published" && pkg.publishedAt) {
    return `Published ${formatDate(pkg.publishedAt)}`;
  }

  if (pkg.status === "rejected" && pkg.reviewedAt) {
    return `Reviewed ${formatDate(pkg.reviewedAt)}`;
  }

  if (pkg.status === "archived") {
    if (pkg.publishedAt) {
      return `Last published ${formatDate(pkg.publishedAt)}`;
    }
    return `Archived ${formatDate(pkg.updatedAt)}`;
  }

  if (pkg.status === "suspended") {
    if (pkg.reviewedAt) {
      return `Suspended ${formatDate(pkg.reviewedAt)}`;
    }
    return `Updated ${formatDate(pkg.updatedAt)}`;
  }

  return `Updated ${formatDate(pkg.updatedAt)}`;
}

function getLifecycleTimeline(pkg: CreatorPackage) {
  const entries: Array<{ label: string; timestamp: string; tone: string }> = [
    {
      label: "Created",
      timestamp: pkg.createdAt,
      tone: "bg-muted text-muted-foreground",
    },
  ];

  if (pkg.status === "review") {
    entries.push({
      label: "In review",
      timestamp: pkg.updatedAt,
      tone: "bg-[var(--color-warning)]/10 text-[var(--color-warning)]",
    });
  }

  if (pkg.status === "rejected" && pkg.reviewedAt) {
    entries.push({
      label: "Rejected",
      timestamp: pkg.reviewedAt,
      tone: "bg-[var(--color-destructive)]/10 text-[var(--color-destructive)]",
    });
  }

  if (pkg.status === "suspended" && pkg.reviewedAt) {
    entries.push({
      label: "Suspended",
      timestamp: pkg.reviewedAt,
      tone: "bg-[var(--color-destructive)]/10 text-[var(--color-destructive)]",
    });
  }

  if (pkg.publishedAt) {
    entries.push({
      label: "Published",
      timestamp: pkg.publishedAt,
      tone: "bg-[var(--color-success)]/10 text-[var(--color-success)]",
    });
  }

  if (pkg.status === "archived") {
    entries.push({
      label: "Archived",
      timestamp: pkg.updatedAt,
      tone: "bg-muted text-muted-foreground",
    });
  }

  const hasUpdatedState =
    pkg.updatedAt !== pkg.createdAt
    && pkg.updatedAt !== pkg.reviewedAt
    && pkg.updatedAt !== pkg.publishedAt;

  if (hasUpdatedState) {
    entries.push({
      label: "Last updated",
      timestamp: pkg.updatedAt,
      tone: "bg-muted text-muted-foreground",
    });
  }

  return entries.sort((left, right) => {
    return new Date(left.timestamp).getTime() - new Date(right.timestamp).getTime();
  });
}

function getInstallAdoptionLabel(pkg: CreatorPackage) {
  if (pkg.installs === 0) {
    return "No buyer installs recorded yet.";
  }

  return `${formatNumber(pkg.installAdoption.deployedCount)} deployed · ${formatNumber(pkg.installAdoption.pendingSetupCount)} awaiting buyer setup`;
}

function getReviewPipelineLabel(pkg: CreatorPackage) {
  if (pkg.reviewPipeline.pendingCount === 0) {
    return "No buyer reviews are currently waiting on moderation.";
  }

  return `${formatNumber(pkg.reviewPipeline.pendingCount)} buyer review${pkg.reviewPipeline.pendingCount === 1 ? "" : "s"} pending moderation`;
}

type CreatorFollowThroughItem = {
  id: string;
  packageId: string;
  packageName: string;
  priority: number;
  title: string;
  description: string;
  action:
    | { kind: "lifecycle"; label: string; value: "submit_for_review" | "withdraw_to_draft" | "archive_listing" }
    | { kind: "link"; label: string; to: string }
    | null;
};

function buildFollowThroughQueue(packages: CreatorPackage[]): CreatorFollowThroughItem[] {
  return packages
    .flatMap<CreatorFollowThroughItem>((pkg) => {
      const items: CreatorFollowThroughItem[] = [];

      if ((pkg.status === "rejected" || pkg.status === "suspended") && pkg.lifecycle.availableActions.includes("submit_for_review")) {
        items.push({
          id: `${pkg.id}-resubmit`,
          packageId: pkg.id,
          packageName: pkg.name,
          priority: 1,
          title: "Needs creator fixes",
          description: pkg.moderationReason
            ?? "Marketplace review returned this package for creator follow-through before it can go live again.",
          action: {
            kind: "lifecycle",
            label: "Resubmit",
            value: "submit_for_review",
          },
        });
      }

      if (pkg.reviewPipeline.pendingCount > 0) {
        items.push({
          id: `${pkg.id}-reviews`,
          packageId: pkg.id,
          packageName: pkg.name,
          priority: 2,
          title: "Buyer trust proof waiting",
          description: `${getReviewPipelineLabel(pkg)}${pkg.reviewPipeline.lastPendingSubmittedAt
            ? ` Latest pending ${formatDate(pkg.reviewPipeline.lastPendingSubmittedAt)}.`
            : ""}`,
          action: pkg.listingPath
            ? {
              kind: "link",
              label: "Review listing",
              to: pkg.listingPath,
            }
            : null,
        });
      }

      if (pkg.installAdoption.pendingSetupCount > 0 && pkg.listingPath) {
        items.push({
          id: `${pkg.id}-installs`,
          packageId: pkg.id,
          packageName: pkg.name,
          priority: 3,
          title: "Buyer installs awaiting setup",
          description: `${formatNumber(pkg.installAdoption.pendingSetupCount)} buyer install${pkg.installAdoption.pendingSetupCount === 1 ? "" : "s"} still need deployment. ${formatNumber(pkg.installAdoption.deployedCount)} already deployed.`,
          action: {
            kind: "link",
            label: "Review listing",
            to: pkg.listingPath,
          },
        });
      }

      if (pkg.status === "review" && pkg.lifecycle.availableActions.includes("withdraw_to_draft")) {
        items.push({
          id: `${pkg.id}-review`,
          packageId: pkg.id,
          packageName: pkg.name,
          priority: 4,
          title: "Marketplace review in progress",
          description: `Version v${pkg.version} is currently in moderation and can still be withdrawn back to draft if you need to revise it before approval.`,
          action: {
            kind: "lifecycle",
            label: "Withdraw",
            value: "withdraw_to_draft",
          },
        });
      }

      return items;
    })
    .sort((left, right) => left.priority - right.priority || left.packageName.localeCompare(right.packageName))
    .slice(0, 5);
}

function buildLifecycleSummary(
  packages: CreatorPackage[],
  previousSummary: CreatorDashboardData["lifecycleSummary"],
): CreatorDashboardData["lifecycleSummary"] {
  const topPerformer = [...packages]
    .sort((left, right) => {
      return right.installs - left.installs
        || right.revenue - left.revenue
        || right.rating - left.rating
        || left.name.localeCompare(right.name);
    })[0] ?? null;

  return {
    liveOffers: packages.filter((pkg) => pkg.status === "published").length,
    inReview: packages.filter((pkg) => pkg.status === "review").length,
    needsAttention: packages.filter((pkg) => pkg.status === "rejected" || pkg.status === "suspended").length,
    archived: packages.filter((pkg) => pkg.status === "archived").length,
    thisMonthRevenue: previousSummary.thisMonthRevenue,
    pendingBuyerReviews: packages.reduce((sum, pkg) => sum + pkg.reviewPipeline.pendingCount, 0),
    pendingBuyerSetup: packages.reduce((sum, pkg) => sum + pkg.installAdoption.pendingSetupCount, 0),
    deployedInstalls: packages.reduce((sum, pkg) => sum + pkg.installAdoption.deployedCount, 0),
    topPerformer: topPerformer
      ? {
        id: topPerformer.id,
        name: topPerformer.name,
        installs: topPerformer.installs,
        revenue: topPerformer.revenue,
        rating: topPerformer.rating,
      }
      : null,
  };
}

export default function CreatorDashboard() {
  const { data, isLoading, error, setData } = useCreatorDashboard();
  const [pendingActionByPackage, setPendingActionByPackage] = useState<Record<string, string>>({});
  const [actionError, setActionError] = useState<string | null>(null);

  async function handleLifecycleAction(
    pkg: CreatorPackage,
    action: "submit_for_review" | "withdraw_to_draft" | "archive_listing",
  ) {
    setActionError(null);
    setPendingActionByPackage((current) => ({ ...current, [pkg.id]: action }));

    try {
      const updatedPackage = await updateCreatorPackageLifecycle(pkg.id, action);
      setData((current) => {
        if (!current) return current;
        const packages = current.packages.map((item) => (item.id === pkg.id ? updatedPackage : item));
        return {
          ...current,
          packages,
          lifecycleSummary: buildLifecycleSummary(packages, current.lifecycleSummary),
        };
      });
    } catch (mutationError) {
      setActionError(mutationError instanceof Error ? mutationError.message : "Failed to update package lifecycle.");
    } finally {
      setPendingActionByPackage((current) => {
        const next = { ...current };
        delete next[pkg.id];
        return next;
      });
    }
  }

  if (isLoading) return <CreatorSkeleton />;

  if (error) {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-[var(--color-destructive)]/20 bg-[var(--color-destructive)]/5 p-4 text-sm text-[var(--color-destructive)]">
        <AlertCircle className="h-4 w-4" />
        Failed to load creator dashboard.
      </div>
    );
  }

  if (!data || data.packages.length === 0) {
    return (
      <EmptyState
        icon={<Package className="h-8 w-8" />}
        title="No marketplace packages yet"
        description="This dashboard now reflects the mounted marketplace tables. Create and publish a package through the existing builder workflow to see it here."
        variant="info"
      />
    );
  }

  const maxRevenue = Math.max(...data.revenueByMonth.map((entry) => entry.revenue), 0);
  const totalApprovedReviews = data.packages.reduce((sum, pkg) => sum + pkg.reviewCount, 0);
  const followThroughQueue = buildFollowThroughQueue(data.packages);

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <h1 className="text-2xl font-semibold text-foreground">Creator Dashboard</h1>
        <p className="text-sm text-muted-foreground">
          This page reads from the live marketplace creator surface. Drafts can move into review here, published offers can be archived back off the marketplace, and later states stay grounded in the mounted listing lifecycle.
        </p>
        <div className="rounded-lg border border-border bg-muted/40 p-3 text-sm text-muted-foreground">
          <p className="font-medium text-foreground">{data.publishingSurface.label}</p>
          <p className="mt-1">{data.publishingSurface.description}</p>
        </div>
        <div className="rounded-lg border border-border bg-background p-4 text-sm text-muted-foreground">
          <div className="flex items-center gap-2 font-medium text-foreground">
            <Copy className="h-4 w-4 text-[var(--color-primary)]" />
            Version delivery model
          </div>
          <ul className="mt-3 space-y-2 text-sm">
            <li>Each published snapshot becomes a versioned marketplace offer, not a live overwrite of installed buyer copies.</li>
            <li>Existing buyer installs stay unchanged when you publish a newer version.</li>
            <li>Upgrades are explicit marketplace re-installs, so review and publish decisions should be made per version.</li>
          </ul>
        </div>
        {actionError ? (
          <div className="rounded-lg border border-[var(--color-destructive)]/20 bg-[var(--color-destructive)]/5 p-3 text-sm text-[var(--color-destructive)]">
            {actionError}
          </div>
        ) : null}
      </div>

      {followThroughQueue.length > 0 ? (
        <section className="rounded-xl border border-border bg-background p-4">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h2 className="text-sm font-medium text-foreground">Creator follow-through queue</h2>
              <p className="mt-1 text-xs text-muted-foreground">
                Prioritized from the mounted creator lifecycle, pending buyer-review moderation, and install-adoption truth so you can see the next creator-visible action without scanning every package row.
              </p>
            </div>
          </div>
          <div className="mt-4 grid grid-cols-1 gap-3 xl:grid-cols-2">
            {followThroughQueue.map((item) => {
              const action = item.action;
              const queuePackage = data.packages.find((pkg) => pkg.id === item.packageId) ?? null;

              return (
                <div
                  key={item.id}
                  className="rounded-lg border border-border/70 bg-muted/20 p-3"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground/80">
                        {item.title}
                      </div>
                      <div className="mt-1 font-medium text-foreground">{item.packageName}</div>
                      <div className="mt-1 text-sm text-muted-foreground">{item.description}</div>
                    </div>
                    {action?.kind === "link" ? (
                      <Link
                        to={action.to}
                        className="inline-flex shrink-0 items-center gap-1 rounded-lg border border-border px-3 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                      >
                        <Eye className="h-3.5 w-3.5" />
                        {action.label}
                      </Link>
                    ) : action?.kind === "lifecycle" && queuePackage ? (
                      <button
                        type="button"
                        onClick={() => void handleLifecycleAction(queuePackage, action.value)}
                        disabled={Boolean(pendingActionByPackage[item.packageId])}
                        className="inline-flex shrink-0 items-center gap-1 rounded-lg border border-border px-3 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        {pendingActionByPackage[item.packageId]
                          ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          : action.value === "withdraw_to_draft"
                            ? <Undo2 className="h-3.5 w-3.5" />
                            : <UploadCloud className="h-3.5 w-3.5" />}
                        {action.label}
                      </button>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      ) : null}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-8">
        <div className="rounded-lg border border-border bg-background p-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <DollarSign className="h-4 w-4" />
            Total Revenue
          </div>
          <div className="mt-2 text-2xl font-bold text-foreground">{formatCurrency(data.totalRevenue * 100, "EUR", { maximumFractionDigits: 0 })}</div>
        </div>
        <div className="rounded-lg border border-border bg-background p-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Download className="h-4 w-4" />
            Total Installs
          </div>
          <div className="mt-2 text-2xl font-bold text-foreground">{formatNumber(data.totalInstalls)}</div>
        </div>
        <div className="rounded-lg border border-border bg-background p-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Star className="h-4 w-4" />
            Average Rating
          </div>
          <div className="mt-2 text-2xl font-bold text-foreground">{data.averageRating.toFixed(1)}</div>
        </div>
        <div className="rounded-lg border border-border bg-background p-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Package className="h-4 w-4" />
            Packages
          </div>
          <div className="mt-2 text-2xl font-bold text-foreground">{data.packages.length}</div>
        </div>
        <div className="rounded-lg border border-border bg-background p-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Star className="h-4 w-4" />
            Approved buyer reviews
          </div>
          <div className="mt-2 text-2xl font-bold text-foreground">{formatNumber(totalApprovedReviews)}</div>
          <div className="mt-1 text-xs text-muted-foreground">
            Public marketplace reviews already approved by moderation and contributing to creator rating proof.
          </div>
        </div>
        <div className="rounded-lg border border-border bg-background p-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Eye className="h-4 w-4" />
            Pending buyer reviews
          </div>
          <div className="mt-2 text-2xl font-bold text-foreground">{formatNumber(data.lifecycleSummary.pendingBuyerReviews)}</div>
          <div className="mt-1 text-xs text-muted-foreground">
            Buyer reviews submitted from installed packages that are still waiting on admin moderation.
          </div>
        </div>
        <div className="rounded-lg border border-border bg-background p-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Download className="h-4 w-4" />
            Deployed installs
          </div>
          <div className="mt-2 text-2xl font-bold text-foreground">{formatNumber(data.lifecycleSummary.deployedInstalls)}</div>
          <div className="mt-1 text-xs text-muted-foreground">
            Buyer installs already deployed into tenant accounts from the mounted marketplace purchase ledger.
          </div>
        </div>
        <div className="rounded-lg border border-border bg-background p-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <AlertCircle className="h-4 w-4" />
            Pending buyer setup
          </div>
          <div className="mt-2 text-2xl font-bold text-foreground">{formatNumber(data.lifecycleSummary.pendingBuyerSetup)}</div>
          <div className="mt-1 text-xs text-muted-foreground">
            Paid marketplace installs that are recorded but not yet deployed into buyer accounts.
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="rounded-lg border border-border bg-background p-4">
          <div className="text-sm text-muted-foreground">Pending payout</div>
          <div className="mt-2 text-2xl font-bold text-foreground">{formatCurrency(data.payoutSummary.pendingPayout * 100, "EUR", { maximumFractionDigits: 0 })}</div>
          <div className="mt-1 text-xs text-muted-foreground">
            Creator-share revenue recorded in the ledger but not yet settled into a paid payout batch.
          </div>
        </div>
        <div className="rounded-lg border border-border bg-background p-4">
          <div className="text-sm text-muted-foreground">Paid out total</div>
          <div className="mt-2 text-2xl font-bold text-foreground">{formatCurrency(data.payoutSummary.paidOutTotal * 100, "EUR", { maximumFractionDigits: 0 })}</div>
          <div className="mt-1 text-xs text-muted-foreground">
            Historical creator-share revenue already marked paid in the mounted revenue-share ledger.
          </div>
        </div>
        <div className="rounded-lg border border-border bg-background p-4">
          <div className="text-sm text-muted-foreground">Last payout batch</div>
          <div className="mt-2 text-base font-semibold text-foreground">
            {data.payoutSummary.lastPayoutBatchId ?? "No paid batch yet"}
          </div>
          <div className="mt-1 text-xs text-muted-foreground">
            {data.payoutSummary.lastPaidAt
              ? `Last paid ${formatDate(data.payoutSummary.lastPaidAt)}`
              : "Auto-payout execution is still broader than the mounted runtime, so this surface only reflects recorded paid batches."}
          </div>
        </div>
      </div>

      <div className="rounded-lg border border-border bg-background p-4">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h2 className="text-sm font-medium text-foreground">Recent payout history</h2>
            <p className="mt-1 text-xs text-muted-foreground">
              This surface reflects only paid batches already recorded in the revenue-share ledger. It does not imply transfer automation beyond those recorded settlements.
            </p>
          </div>
        </div>
        {data.payoutHistory.length > 0 ? (
          <div className="mt-4 space-y-3">
            {data.payoutHistory.map((entry) => (
              <div
                key={entry.payoutBatchId}
                className="flex flex-col gap-2 rounded-md border border-border/70 bg-muted/20 p-3 sm:flex-row sm:items-center sm:justify-between"
              >
                <div>
                  <div className="font-medium text-foreground">{entry.payoutBatchId}</div>
                  <div className="mt-1 text-xs text-muted-foreground">
                    {entry.paidAt ? `Paid ${formatDate(entry.paidAt)}` : "Paid batch recorded"}
                    {" · "}
                    {entry.shareCount} revenue share{entry.shareCount === 1 ? "" : "s"}
                  </div>
                </div>
                <div className="text-sm font-semibold text-foreground">
                  {formatCurrency(entry.amount * 100, entry.currency, { maximumFractionDigits: 0 })}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="mt-4 text-sm text-muted-foreground">
            No paid payout batches are recorded yet.
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-5">
          <div className="rounded-lg border border-border bg-background p-4">
            <div className="text-sm text-muted-foreground">Live offers</div>
            <div className="mt-2 text-2xl font-bold text-foreground">{data.lifecycleSummary.liveOffers}</div>
            <div className="mt-1 text-xs text-muted-foreground">Published packages currently visible in the marketplace.</div>
          </div>
          <div className="rounded-lg border border-border bg-background p-4">
            <div className="text-sm text-muted-foreground">In review</div>
            <div className="mt-2 text-2xl font-bold text-foreground">{data.lifecycleSummary.inReview}</div>
            <div className="mt-1 text-xs text-muted-foreground">Submitted versions waiting on marketplace moderation.</div>
          </div>
          <div className="rounded-lg border border-border bg-background p-4">
            <div className="text-sm text-muted-foreground">Needs attention</div>
            <div className="mt-2 text-2xl font-bold text-foreground">{data.lifecycleSummary.needsAttention}</div>
            <div className="mt-1 text-xs text-muted-foreground">Rejected or suspended offers that still need creator follow-through.</div>
          </div>
          <div className="rounded-lg border border-border bg-background p-4">
            <div className="text-sm text-muted-foreground">Archived offers</div>
            <div className="mt-2 text-2xl font-bold text-foreground">{data.lifecycleSummary.archived}</div>
            <div className="mt-1 text-xs text-muted-foreground">Packages intentionally taken off the live marketplace while preserving their historical performance.</div>
          </div>
          <div className="rounded-lg border border-border bg-background p-4">
            <div className="text-sm text-muted-foreground">This month revenue</div>
            <div className="mt-2 text-2xl font-bold text-foreground">{formatCurrency(data.lifecycleSummary.thisMonthRevenue * 100, "EUR", { maximumFractionDigits: 0 })}</div>
            <div className="mt-1 text-xs text-muted-foreground">Creator share recorded in this calendar month from the live revenue-share ledger.</div>
          </div>
        </div>

        <div className="rounded-lg border border-border bg-background p-4">
          <h2 className="text-sm font-medium text-foreground">Top performer</h2>
          {data.lifecycleSummary.topPerformer ? (
            <div className="mt-3 space-y-3">
              <div>
                <div className="text-base font-semibold text-foreground">{data.lifecycleSummary.topPerformer.name}</div>
                <div className="text-xs text-muted-foreground">
                  Ranked by live installs first, then creator revenue and buyer rating.
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3 text-sm">
                <div className="rounded-md border border-border/70 bg-muted/20 p-3">
                  <div className="text-xs text-muted-foreground">Installs</div>
                  <div className="mt-1 font-semibold text-foreground">
                    {formatNumber(data.lifecycleSummary.topPerformer.installs)}
                  </div>
                </div>
                <div className="rounded-md border border-border/70 bg-muted/20 p-3">
                  <div className="text-xs text-muted-foreground">Revenue</div>
                  <div className="mt-1 font-semibold text-foreground">
                    {formatCurrency(data.lifecycleSummary.topPerformer.revenue * 100, "EUR", { maximumFractionDigits: 0 })}
                  </div>
                </div>
                <div className="rounded-md border border-border/70 bg-muted/20 p-3">
                  <div className="text-xs text-muted-foreground">Rating</div>
                  <div className="mt-1 font-semibold text-foreground">
                    {data.lifecycleSummary.topPerformer.rating.toFixed(1)}
                  </div>
                </div>
              </div>
              <div className="text-xs text-muted-foreground">
                Archived packages remain counted in historical performance, but only published offers contribute to the live-offer summary above.
              </div>
            </div>
          ) : (
            <div className="mt-3 text-sm text-muted-foreground">
              Top performer will appear here once at least one marketplace package has live install or revenue history.
            </div>
          )}
        </div>
      </div>

      {data.revenueByMonth.length > 0 ? (
        <div className="rounded-lg border border-border bg-background p-4">
          <h2 className="mb-3 flex items-center gap-2 text-sm font-medium text-foreground">
            <BarChart3 className="h-4 w-4 text-muted-foreground" />
            Revenue Trend
          </h2>
          <div className="flex h-32 items-end gap-2">
            {data.revenueByMonth.map((month) => {
              const height = maxRevenue > 0 ? (month.revenue / maxRevenue) * 100 : 0;
              return (
                <div key={month.month} className="flex flex-1 flex-col items-center gap-1">
                  <span className="text-[10px] text-muted-foreground">{formatCurrency(month.revenue * 100, "EUR", { maximumFractionDigits: 0 })}</span>
                  <div
                    className="w-full rounded-t bg-[var(--color-primary)]/70"
                    style={{ height: `${height}%`, minHeight: "4px" }}
                  />
                  <span className="text-[10px] text-muted-foreground">{month.month}</span>
                </div>
              );
            })}
          </div>
        </div>
      ) : null}

      <div className="overflow-hidden rounded-lg border border-border bg-background">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border">
                <th className="px-4 py-3 text-left font-medium text-muted-foreground">Package</th>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground">Status</th>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground">Installs</th>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground">Revenue</th>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground">Rating</th>
                <th className="px-4 py-3 text-right font-medium text-muted-foreground">Surface</th>
              </tr>
            </thead>
            <tbody>
              {data.packages.map((pkg) => (
                <tr key={pkg.id} className="border-b border-border/50 last:border-0 hover:bg-muted/30">
                  <td className="px-4 py-3">
                    <div>
                      <div className="font-medium text-foreground">{pkg.name}</div>
                      <div className="text-xs text-muted-foreground">v{pkg.version}</div>
                      <div className="mt-1 max-w-sm text-xs text-muted-foreground">
                        {getVersionOfferHint(pkg)}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="space-y-2">
                      <StatusBadge status={pkg.status} />
                      <div className="max-w-xs text-xs text-muted-foreground">
                        {getLifecycleNote(pkg)}
                      </div>
                      <div className="text-[11px] text-muted-foreground/80">
                        {getLifecycleTimestamp(pkg)}
                      </div>
                      <div className="max-w-xs space-y-1 pt-1">
                        <div className="text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground/80">
                          Lifecycle timeline
                        </div>
                        <div className="space-y-1">
                          {getLifecycleTimeline(pkg).map((entry) => (
                            <div
                              key={`${pkg.id}-${entry.label}-${entry.timestamp}`}
                              className="flex items-center justify-between gap-3 rounded-md border border-border/70 bg-muted/20 px-2 py-1.5 text-[11px]"
                            >
                              <span className={`inline-flex rounded-full px-2 py-0.5 font-medium ${entry.tone}`}>
                                {entry.label}
                              </span>
                              <span className="text-muted-foreground">
                                {formatDate(entry.timestamp)}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="space-y-1">
                      <div className="font-medium text-foreground">{formatNumber(pkg.installs)}</div>
                      <div className="max-w-[14rem] text-[11px] text-muted-foreground">
                        {getInstallAdoptionLabel(pkg)}
                      </div>
                      {pkg.installAdoption.lastPurchasedAt ? (
                        <div className="text-[11px] text-muted-foreground/80">
                          Last purchase {formatDate(pkg.installAdoption.lastPurchasedAt)}
                        </div>
                      ) : null}
                      {pkg.installAdoption.lastDeployedAt ? (
                        <div className="text-[11px] text-muted-foreground/80">
                          Last deployed {formatDate(pkg.installAdoption.lastDeployedAt)}
                        </div>
                      ) : null}
                    </div>
                  </td>
                  <td className="px-4 py-3 font-medium text-foreground">{formatCurrency(pkg.revenue * 100, "EUR", { maximumFractionDigits: 0 })}</td>
                  <td className="px-4 py-3">
                    <div className="space-y-2">
                      <div className="flex items-center gap-1">
                        <Star className="h-3 w-3 fill-amber-400 text-[var(--color-warning)]/90" />
                        <span>{pkg.rating.toFixed(1)}</span>
                        <span className="text-xs text-muted-foreground">({pkg.reviewCount})</span>
                      </div>
                      {pkg.recentReviews.length > 0 ? (
                        <div className="max-w-xs space-y-1">
                          {pkg.recentReviews.map((review, index) => (
                            <div key={`${pkg.id}-review-${index}`} className="rounded-md border border-border/70 bg-muted/30 px-2 py-1.5">
                              <div className="flex items-center gap-1 text-[11px] text-muted-foreground">
                                <Star className="h-3 w-3 fill-amber-400 text-[var(--color-warning)]/90" />
                                <span>{review.rating.toFixed(1)}</span>
                                <span>{formatDate(review.createdAt)}</span>
                              </div>
                              <div className="mt-1 text-[11px] text-muted-foreground">
                                {review.text?.trim() || "Buyer submitted a rating without review text."}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-[11px] text-muted-foreground/80">
                          No approved buyer reviews yet.
                        </div>
                      )}
                      <div className="rounded-md border border-border/70 bg-muted/20 px-2 py-1.5 text-[11px] text-muted-foreground">
                        {getReviewPipelineLabel(pkg)}
                        {pkg.reviewPipeline.lastPendingSubmittedAt ? (
                          <div className="mt-1 text-muted-foreground/80">
                            Latest pending {formatDate(pkg.reviewPipeline.lastPendingSubmittedAt)}
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right">
                    {pkg.status === "published" ? (
                      <div className="flex justify-end gap-2">
                        {pkg.listingPath ? (
                          <Link
                            to={pkg.listingPath}
                            className="inline-flex items-center gap-1 rounded-lg border border-border px-3 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                          >
                            <Eye className="h-3.5 w-3.5" />
                            Review listing
                          </Link>
                        ) : null}
                        {pkg.lifecycle.availableActions.includes("archive_listing") ? (
                          <button
                            type="button"
                            onClick={() => void handleLifecycleAction(pkg, "archive_listing")}
                            disabled={Boolean(pendingActionByPackage[pkg.id])}
                            className="inline-flex items-center gap-1 rounded-lg border border-border px-3 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:cursor-not-allowed disabled:opacity-60"
                          >
                            {pendingActionByPackage[pkg.id] ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Archive className="h-3.5 w-3.5" />}
                            Archive
                          </button>
                        ) : null}
                      </div>
                    ) : pkg.lifecycle.availableActions.includes("submit_for_review") ? (
                      <button
                        type="button"
                        onClick={() => void handleLifecycleAction(pkg, "submit_for_review")}
                        disabled={Boolean(pendingActionByPackage[pkg.id])}
                        className="inline-flex items-center gap-1 rounded-lg bg-[var(--color-primary)] px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-[var(--color-primary)]/90 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        {pendingActionByPackage[pkg.id] ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <UploadCloud className="h-3.5 w-3.5" />}
                        Submit for review
                      </button>
                    ) : pkg.lifecycle.availableActions.includes("withdraw_to_draft") ? (
                      <button
                        type="button"
                        onClick={() => void handleLifecycleAction(pkg, "withdraw_to_draft")}
                        disabled={Boolean(pendingActionByPackage[pkg.id])}
                        className="inline-flex items-center gap-1 rounded-lg border border-border px-3 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        {pendingActionByPackage[pkg.id] ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Undo2 className="h-3.5 w-3.5" />}
                        Withdraw
                      </button>
                    ) : (
                      <span className="text-xs text-muted-foreground">
                        Awaiting lifecycle change
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
